import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, Button, Alert, Sheet, Tabs, TabList, Tab, TabPanel, Textarea, FormControl, FormLabel, Radio, RadioGroup, Input, Select, Option, Table, CircularProgress, Chip } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';
import { PageHeader, SectionCard } from '../../components/admin/Page.tsx';
import UserInboxModal from '../../components/admin/UserInboxModal.tsx';
import type { UserListItem } from '../../api/admin.ts';

type Mode = 'file' | 'text';

interface BulkFormProps {
  hint: string;
  onSubmit: (input: { file?: File; data?: string }) => Promise<string>;
}

function BulkForm({ hint, onSubmit }: BulkFormProps) {
  const [mode, setMode] = useState<Mode>('text');
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setResult(''); setBusy(true);
    try {
      const r = await onSubmit(mode === 'file' ? { file: file || undefined } : { data: text });
      setResult(r);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography level="body-sm" color="neutral">{hint}</Typography>
      {error && <Alert color="danger" variant="soft">{error}</Alert>}
      <FormControl>
        <FormLabel>Input mode</FormLabel>
        <RadioGroup orientation="horizontal" value={mode} onChange={e => setMode(e.target.value as Mode)}>
          <Radio value="text" label="Text" />
          <Radio value="file" label="File" />
        </RadioGroup>
      </FormControl>
      {mode === 'text' && (
        <FormControl>
          <FormLabel>Lines (format: user|pass)</FormLabel>
          <Textarea minRows={6} maxRows={20} value={text} onChange={e => setText(e.target.value)}
            placeholder={'user1@domain.com|password1\nuser2@domain.com|password2'} />
        </FormControl>
      )}
      {mode === 'file' && (
        <FormControl>
          <FormLabel>Upload file (.txt)</FormLabel>
          <input type="file" accept=".txt,text/plain" onChange={e => setFile(e.target.files?.[0] || null)} />
        </FormControl>
      )}
      <Box>
        <Button type="submit" onClick={handleSubmit} loading={busy}>Submit</Button>
      </Box>
      {result && (
        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{result}</Typography>
        </Sheet>
      )}
    </Stack>
  );
}

function UserList() {
  const { api } = useAdmin();
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inboxUser, setInboxUser] = useState<string | null>(null);

  useEffect(() => {
    api.getSettings().then(s => setDomains(s.domains)).catch(() => {});
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    const t = setTimeout(() => {
      api.listUsers({ search, domain, page, pageSize })
        .then(r => { if (!cancelled) { setItems(r.items); setTotal(r.total); } })
        .catch(err => { if (!cancelled) setError(err.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [api, search, domain, page, pageSize]);

  useEffect(() => { setPage(1); }, [search, domain, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const fromIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const toIdx = Math.min(page * pageSize, total);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-end' }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Search username</FormLabel>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="prefix or substring" />
        </FormControl>
        <FormControl sx={{ minWidth: 220 }}>
          <FormLabel>Domain</FormLabel>
          <Select value={domain} onChange={(_, v) => setDomain((v as string) || '')} placeholder="All domains">
            <Option value="">All domains</Option>
            {domains.map(d => <Option key={d} value={d}>{d}</Option>)}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert color="danger" variant="soft">{error}</Alert>}

      <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'hidden' }}>
        <Table hoverRow stickyHeader sx={{ '& thead th': { bgcolor: 'background.level1', fontWeight: 600 } }}>
          <thead>
            <tr>
              <th style={{ width: '50%' }}>Username</th>
              <th style={{ width: '30%' }}>Domain</th>
              <th style={{ width: '20%', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24 }}><CircularProgress size="sm" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--joy-palette-neutral-500)' }}>No users found</td></tr>
            ) : items.map(u => (
              <tr key={`${u.username}@${u.domain}`}>
                <td>{u.username}</td>
                <td><Chip size="sm" variant="soft">{u.domain}</Chip></td>
                <td style={{ textAlign: 'right' }}>
                  <Button size="sm" variant="soft" onClick={() => setInboxUser(u.username)}>
                    View Inbox
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.level1', flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography level="body-sm" color="neutral">Rows per page:</Typography>
            <Select size="sm" value={pageSize} onChange={(_, v) => setPageSize(Number(v) || 10)} sx={{ minWidth: 80 }}>
              {[10, 30, 50, 100].map(n => <Option key={n} value={n}>{n}</Option>)}
            </Select>
          </Stack>
          <Typography level="body-sm" color="neutral" sx={{ flex: 1 }}>
            {fromIdx}–{toIdx} of {total.toLocaleString()}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button size="sm" variant="outlined" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Typography level="body-sm">Page {page} / {totalPages}</Typography>
            <Button size="sm" variant="outlined" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </Stack>
        </Box>
      </Sheet>

      <UserInboxModal username={inboxUser} onClose={() => setInboxUser(null)} />
    </Stack>
  );
}

export default function Users() {
  const { api } = useAdmin();

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageHeader title="Users" subtitle="Browse mailboxes, view inboxes, and bulk manage accounts" />
      <Tabs defaultValue={0}>
        <TabList sx={{ mb: 2 }}>
          <Tab>List Users</Tab>
          <Tab>Bulk Create</Tab>
          <Tab>Bulk Change Password</Tab>
        </TabList>
        <TabPanel value={0} sx={{ p: 0 }}>
          <UserList />
        </TabPanel>
        <TabPanel value={1} sx={{ p: 0 }}>
          <SectionCard title="Bulk Create Users">
            <BulkForm
              hint="Each line: user@domain|password. Domain must be in allowed domains."
              onSubmit={(input) => api.bulkCreateUsers(input)}
            />
          </SectionCard>
        </TabPanel>
        <TabPanel value={2} sx={{ p: 0 }}>
          <SectionCard title="Bulk Change Password">
            <BulkForm
              hint="Each line: user@domain|newpassword. Only existing users will be updated."
              onSubmit={(input) => api.bulkChangePassword(input)}
            />
          </SectionCard>
        </TabPanel>
      </Tabs>
    </Box>
  );
}
